import { useState, useCallback } from 'react';
import { Promise } from 'bluebird';

import { CSMLSortBy, DEFAULT_PAGE_SIZE, ROOT_FOLDER_ID } from '../../model';

/**
 * floderPathTree:
 *   [{
 *    folderId,
 *    folderName,
 *    subFolders: [{}] // recursive
 *    subFoldersCount,
 * }]
 *
 */

export const useFolderPathTree = ({ repository, defaultPerfMetricDateRange }) => {
  const [folderPathTree, setFolderPathTree] = useState([]);

  const updateFolderPathTree = useCallback((currentFolderPathTree, folderIdArr, subFoldersArr, totalSubfoldersCount = [], reload = false) => {
    const recursiveUpdate = items => items.map((item) => {
      if (folderIdArr.includes(item.folderId)) {
        const index = folderIdArr.indexOf(item.folderId);
        if (reload) {
          return {
            ...item,
            subFolders: subFoldersArr[index],
            subFoldersCount: totalSubfoldersCount[index],
          };
        }
        const newSubFloders = [...item.subFolders, ...subFoldersArr[index]];
        return {
          ...item,
          subFolders: newSubFloders,
        };
      }
      return {
        ...item,
        subFolders: item.subFolders.length > 0 ? recursiveUpdate(item.subFolders) : item.subFolders,
      };
    });

    return recursiveUpdate(currentFolderPathTree);
  }, []);

  const loadFolderPathTreeItemImp = useCallback(({
    folderId,
    skip,
  // eslint-disable-next-line arrow-body-style
  }) => {
    return repository.getFolderData({
      perfMetricDateRange: defaultPerfMetricDateRange,
      sortBy: CSMLSortBy.ImageName,
      skip: skip || 0,
      count: DEFAULT_PAGE_SIZE,
      folderId,
      includeAssets: false,
      includeSubfolders: true,
      includeParentHierarchy: true,
      assetsToLoadForSubfolders: false,
      isAssetPickerScenario: false,
      withBIData: false,
    }).then((res) => {
      const { folder } = res;
      const newFolders = (folder.subfolders || []).map(subFolder => ({
        folderId: subFolder.folderId,
        folderName: subFolder.folderName,
        subFolders: [],
        subFoldersCount: subFolder.itemCount.SubFoldersCount,
        parentHierarchy: subFolder.parentHierarchy,
      }));
      const totalSubfoldersCount = folder.itemCount.SubFoldersCount || 0;
      return {
        subFolders: newFolders,
        totalSubfoldersCount,
      };
    });
  }, [defaultPerfMetricDateRange, repository]);

  const initializeFolderPathTree = useCallback(() => loadFolderPathTreeItemImp({
    folderId: ROOT_FOLDER_ID,
  }).then(({
    subFolders,
    totalSubfoldersCount,
  }) => {
    setFolderPathTree([{
      folderId: ROOT_FOLDER_ID,
      subFolders,
      subFoldersCount: totalSubfoldersCount,
      folderName: 'Root',
      parentHierarchy: [],
    }]);
  }), [loadFolderPathTreeItemImp]);

  const loadFolderPathTreeItem = useCallback(folderId => loadFolderPathTreeItemImp({
    folderId,
  }).then(({
    subFolders,
  }) => {
    setFolderPathTree(currentFolderPathTree => updateFolderPathTree(currentFolderPathTree, [folderId], [subFolders]));
  }), [loadFolderPathTreeItemImp, updateFolderPathTree]);

  const loadMoreFolderPathTreeItem = useCallback((folderId, currentFolderCount) => loadFolderPathTreeItemImp({
    folderId,
    skip: currentFolderCount,
  }).then(({
    subFolders,
  }) => {
    setFolderPathTree(currentFolderPathTree => updateFolderPathTree(currentFolderPathTree, [folderId], [subFolders]));
  }), [loadFolderPathTreeItemImp, updateFolderPathTree]);

  const reloadFolderPathTreeItem = useCallback(folderIdArr => Promise.all(folderIdArr.map(folderId => loadFolderPathTreeItemImp({
    folderId,
  }).then(({
    subFolders,
    totalSubfoldersCount,
  }) => ({
    subFolders, totalSubfoldersCount,
  })))).then((result) => {
    const subFoldersArr = result.map(({ subFolders }) => subFolders);
    const totalSubfoldersCountArr = result.map(({ totalSubfoldersCount }) => totalSubfoldersCount);
    setFolderPathTree(currentFolderPathTree => updateFolderPathTree(currentFolderPathTree, folderIdArr, subFoldersArr, totalSubfoldersCountArr, true));
  }), [loadFolderPathTreeItemImp, updateFolderPathTree]);

  return {
    folderPathTree,
    loadFolderPathTreeItem,
    loadMoreFolderPathTreeItem,
    initializeFolderPathTree,
    reloadFolderPathTreeItem,
  };
};
